import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { MYPAGE_SUBSCRIPTION } from "@/settings/constant";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { ApiErrorCode, getApiErrorCode } from "src/api";
import { useDialogStore } from "src/store/useDialogStore";
const DIALOG_ID = "library-access-blocked";
// MX-646 / MX-647 — 구독 결제 실패 팝업의 고정 문구. 지표 값과 화면 문구가 갈라지지 않도록 한곳에서 쓴다
const SUBSCRIPTION_EXPIRED_TITLE = "결제가 완료되지 않았어요.";
const SUBSCRIPTION_EXPIRED_BODY = "잔액 부족 등의 이유로 이번 달 구독 결제가 실패했어요. 결제를 완료하면 보유 중인 자료를 다시 이용할 수 있어요.";
/**
 * 자료 이용이 막혔을 때의 안내 다이얼로그.
 *
 * **차단 판정은 호출부가 한다** — API 마다 차단을 알리는 방식이 다르기 때문이다.
 * 에러로 오는 API 는 status(403)로, `check-can-open` 은 200 응답의 `canOpen: false` 로 판정한다.
 * 이 훅은 넘겨받은 에러 객체 또는 사유 코드 문자열에서 **문구만** 고른다.
 *
 * 모르는 코드는 일반 안내로 폴백한다 — 구독이 없는 유저에게 구독 결제 문구가 나가면 안 되기 때문이다.
 *
 * 같은 문구를 자료보관함 상세 진입·리스트 열람·다운로드가 공유하므로 여기 한 곳에서만 관리한다.
 * (뷰어 페이지는 팝업 창이라 취소가 `self.close()` 이고 이미 다이얼로그를 인라인 관리해 이 훅을 쓰지 않는다)
 *
 * mixpanel(MX-646 / MX-647)은 구독 결제 실패 분기에서만 발사한다. 일반 안내는 지표 대상이 아니다.
 */ const useLibraryBlockedDialog = ()=>{
    const router = useRouter();
    const { open , close  } = useDialogStore();
    return useCallback(function(error) {
        let { onClose , position , productId  } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        const handleClose = ()=>{
            close(DIALOG_ID);
            onClose === null || onClose === void 0 ? void 0 : onClose();
        };
        // 월구독 결제 실패·만료
        if (getApiErrorCode(error) === ApiErrorCode.SubscriptionExpired) {
            // MX-646 / MX-647 공통 Property.
            // TODO(지표): 'Grace Day Count'(유예기간 경과 일수) 미포함. 차단 판정이 403 에러코드뿐이라
            // 프론트가 경과 일수를 알 수 없다. BE 가 응답에 실어주면 여기에 추가한다.
            const popupProps = {
                Position: position,
                "Popup Type": "구독 결제 실패",
                "Popup Title": SUBSCRIPTION_EXPIRED_TITLE,
                ...productId !== undefined && {
                    "Product Id": productId
                }
            };
            if (position) {
                mixpanelEvent("Popup Viewed", {
                    ...popupProps,
                    Body: SUBSCRIPTION_EXPIRED_BODY
                });
            }
            const trackButtonClick = (buttonName, buttonUrl)=>{
                if (!position) return;
                mixpanelEvent("Popup Button Clicked", {
                    ...popupProps,
                    "Button Name": buttonName,
                    "Button URL": buttonUrl
                });
            };
            open({
                id: DIALOG_ID,
                type: "confirm",
                props: {
                    severity: "error",
                    title: SUBSCRIPTION_EXPIRED_TITLE,
                    description: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "잔액 부족 등의 이유로 이번 달 구독 결제가 실패했어요.",
                            /*#__PURE__*/ _jsx("br", {}),
                            "결제를 완료하면 보유 중인 자료를 다시 이용할 수 있어요."
                        ]
                    }),
                    cancelText: "닫기",
                    confirmText: "결제 완료하기",
                    onConfirm: ()=>{
                        trackButtonClick("결제 완료하기", MYPAGE_SUBSCRIPTION);
                        router.push(MYPAGE_SUBSCRIPTION);
                    },
                    onCancel: ()=>{
                        trackButtonClick("닫기", null);
                        handleClose();
                    }
                }
            });
            return;
        }
        // TODO(BE 확인): 엑스퍼트 핸드아웃 다운로드 불가도 403 으로 전환 예정.
        // 코드 리터럴이 확정되면 전용 문구로 분기한다. 그때까지는 아래 일반 안내로 폴백한다.
        open({
            id: DIALOG_ID,
            type: "confirm",
            props: {
                severity: "error",
                title: "이용할 수 없는 자료예요.",
                description: "이용 권한을 확인해주세요.",
                confirmText: "확인",
                onConfirm: handleClose
            }
        });
    }, [
        open,
        close,
        router
    ]);
};
export default useLibraryBlockedDialog;
